/**
 * WordPress dependencies
 */
import { registerFormatType } from '@wordpress/rich-text';
import { RichTextToolbarButton } from '@wordpress/block-editor';
import { __ } from '@wordpress/i18n';
import { useState } from '@wordpress/element';
import { Popover, Button, TextControl, SelectControl, ToggleControl } from '@wordpress/components';

/**
 * Arrow Link Format
 */
const ArrowLinkFormat = ({ isActive, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [url, setUrl] = useState('#');
  const [color, setColor] = useState('primary');
  const [underline, setUnderline] = useState(true);
  const [alt, setAlt] = useState('');
  const [linkText, setLinkText] = useState('');

  const openDialog = () => {
    // Get selected text when opening the dialog
    if (value && value.start !== undefined && value.end !== undefined) {
      const selectedText = value.text.substring(value.start, value.end);
      setLinkText(selectedText);
    } else {
      setLinkText('');
    }
    setIsOpen(true);
  };

  const applyFormat = () => {
    console.log('applyFormat called', { value, url, color, alt, underline, linkText });
    
    if (!value || !value.text) {
      console.log('No value or text');
      return;
    }
    
    if (!linkText.trim()) {
      console.log('No link text provided');
      return;
    }
    
    // Create the shortcode with attributes
    const shortcodeAttrs = [];
    if (url && url !== '#') shortcodeAttrs.push(`url="${url}"`);
    if (color && color !== 'primary') shortcodeAttrs.push(`color="${color}"`);
    if (alt) shortcodeAttrs.push(`alt="${alt}"`);
    if (!underline) shortcodeAttrs.push('class="no-underline"');
    
    const shortcode = `[arrow_link ${shortcodeAttrs.join(' ')}]${linkText}[/arrow_link]`;
    console.log('Generated shortcode:', shortcode);
    
    // Get current text and cursor position
    const currentText = value.text || '';
    const cursorPos = value.start || currentText.length;
    
    // Insert shortcode at cursor position
    const beforeText = currentText.substring(0, cursorPos);
    const afterText = currentText.substring(cursorPos);
    const newText = beforeText + shortcode + afterText;
    
    // Calculate new cursor position (after the inserted shortcode)
    // Make sure it doesn't exceed the text length
    const newCursorPos = Math.min(cursorPos + shortcode.length, newText.length);
    
    // Ensure cursor position is valid
    const validCursorPos = Math.max(0, Math.min(newCursorPos, newText.length));
    
    const newValue = {
      ...value,
      text: newText,
      start: validCursorPos,
      end: validCursorPos
    };
    
    console.log('Before text:', beforeText);
    console.log('After text:', afterText);
    console.log('New text:', newText);
    console.log('New text length:', newText.length);
    console.log('Calculated cursor pos:', newCursorPos);
    console.log('Valid cursor pos:', validCursorPos);
    console.log('New value:', newValue);
    
    onChange(newValue);
    setIsOpen(false);
  };

  return (
    <>
      <RichTextToolbarButton
        icon="arrow-right-alt2"
        title={__('Arrow Link', 'puroclean')}
        onClick={openDialog}
        isActive={isActive}
      />
      
      {isOpen && (
        <Popover
          position="bottom center"
          onClose={() => setIsOpen(false)}
        >
          <div style={{ padding: '16px', minWidth: '300px' }}>
            <h4 style={{ margin: '0 0 16px 0' }}>{__('Arrow Link Settings', 'puroclean')}</h4>
            
            <TextControl
              label={__('Link Text', 'puroclean')}
              value={linkText}
              onChange={setLinkText}
              placeholder="Enter link text..."
              help={__('The text that will be displayed as the link', 'puroclean')}
            />
            
            <TextControl
              label={__('URL', 'puroclean')}
              value={url}
              onChange={setUrl}
              placeholder="https://example.com"
            />
            
            <SelectControl
              label={__('Color', 'puroclean')}
              value={color}
              options={[
                { label: __('Primary (Red)', 'puroclean'), value: 'primary' },
                { label: __('Secondary (Blue)', 'puroclean'), value: 'blue' },
                { label: __('Black', 'puroclean'), value: 'black' },
                { label: __('White', 'puroclean'), value: 'white' }
              ]}
              onChange={setColor}
            />
            
            <TextControl
              label={__('Alt Text', 'puroclean')}
              value={alt}
              onChange={setAlt}
              placeholder="Description for accessibility"
            />
            
            <ToggleControl
              label={__('Underline', 'puroclean')}
              checked={underline}
              onChange={setUnderline}
            />
            
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }}>
              <Button isPrimary onClick={applyFormat}>
                {__('Apply', 'puroclean')}
              </Button>
              <Button onClick={() => setIsOpen(false)}>
                {__('Cancel', 'puroclean')}
              </Button>
            </div>
          </div>
        </Popover>
      )}
    </>
  );
};

// Register the format
registerFormatType('puroclean/arrow-link', {
  title: __('Arrow Link', 'puroclean'),
  tagName: 'span',
  className: 'arrow-link-format',
  edit: ArrowLinkFormat,
});
